import { PlusIcon } from '@heroicons/react/24/outline';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Textarea, TextField } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@shared/components/Tooltip';
import AddUserModal from '@/shared/layout/createLayout/CreateModal';
import StepperPageShell from '@/shared/layout/StepperPageShell';
import StepTabNav from '@/shared/components/StepTabNav';
import ErrorPage403 from '@/app/routes/errorPage403';
import Loader from '@/app/routes/Loader';
import { useEffect, useMemo, useRef } from 'react';
import {
  useCreateSubscription,
  PERIOD_TYPE_OPTIONS,
  PERIOD_OPTIONS_MONTHLY,
  PERIOD_OPTIONS_YEARLY,
  STEP_TABS,
  EQUIPMENT_RANK_COUNT,
  MAX_PRICE_LENGTH,
  isPriceTooBig,
  type CreateSubscriptionFormProps,
  type FormErrors,
} from '@/epics/subscriptionManagment/hooks/useCreateSubscription';
import { OfferInfiniteDropdown } from '@/epics/subscriptionManagment/components/OfferInfiniteDropdown';
import { AppliedOfferCard } from '@/epics/subscriptionManagment/components/AppliedOfferCard';
import { ServiceConfigCard } from '@/epics/subscriptionManagment/components/ServiceConfigCard';
import { RemoveServiceDialog } from '@/epics/subscriptionManagment/components/RemoveServiceDialog';
import { EquipmentCategoryPriceRow } from '@/epics/subscriptionManagment/components/EquipmentCategoryPriceRow';
import { DiscountTierRow } from '@/epics/subscriptionManagment/components/DiscountTierRow';
import { PlanPreviewAccordion } from '@/epics/subscriptionManagment/components/PlanPreviewAccordion';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';

// Visual top-to-bottom order of Step 1 fields — used to find the first
// erroring field to scroll to, same convention as AddClient.tsx.
const STEP1_FIELD_ORDER: (keyof FormErrors)[] = [
  'planName',
  'description',
  'durationType',
  'period',
  'cycle',
  'price',
  'userCount',
  'facilityCount',
];

const CreateSubscription = () => {
  const {
    step,
    maxReachedStep,
    completedSteps,
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,
    offerId,
    setOfferId,
    offerName,
    offerDiscount,
    setOfferDiscount,
    setOfferName,
    services,
    expandedServiceKey,
    serviceFieldErrors,
    servicesError,
    hasIncompleteService,
    removeDialogEntry,
    handleAddService,
    handleServiceTypeChange,
    handleServiceFieldChange,
    handleToggleExpandService,
    handleRequestRemoveService,
    handleCloseRemoveDialog,
    handleConfirmRemoveService,
    equipmentCategoryOptions,
    equipmentCategoriesLoading,
    equipmentPrices,
    equipmentPriceErrors,
    handleEquipmentPriceChange,
    discountTiers,
    discountTierErrors,
    handleDiscountTierChange,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    isPreviewStep,
    previewData,
    handleSubmit,
    handleCancel,
    handleStepTabClick,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    handleBackToEdit,
    formatPriceWithSpaces,
    getBillingDescription,
  } = useCreateSubscription();

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  // Auto-scroll to the first error field whenever a submit/tab-click
  // validation attempt produces an error
  const submitRequestedRef = useRef(false);

  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    const firstErrorField = STEP1_FIELD_ORDER.find((f) => Boolean(errors[f]));
    if (firstErrorField) {
      submitRequestedRef.current = false;
      document
        .querySelector(`[data-field="subscription-${firstErrorField}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  useEffect(() => {
    if (!submitRequestedRef.current || !servicesError) {
      return;
    }
    submitRequestedRef.current = false;
    const target = expandedServiceKey
      ? document.querySelector(`[data-field="service-${expandedServiceKey}"]`)
      : document.querySelector('[data-field="subscription-services-error"]');
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [servicesError, expandedServiceKey]);

  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    const firstErrorCategoryId = Object.keys(equipmentPriceErrors)[0];
    if (!firstErrorCategoryId) {
      return;
    }
    submitRequestedRef.current = false;
    document
      .querySelector(`[data-field="equipment-price-${firstErrorCategoryId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [equipmentPriceErrors]);

  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    const firstErrorRank = Object.keys(discountTierErrors)[0];
    if (!firstErrorRank) {
      return;
    }
    submitRequestedRef.current = false;
    document
      .querySelector(`[data-field="discount-tier-${firstErrorRank}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [discountTierErrors]);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    submitRequestedRef.current = true;
    void handleSubmit(e);
  };

  const handleTabClick = (targetStep: number) => {
    submitRequestedRef.current = true;
    handleStepTabClick(targetStep);
  };

  const PlanDetailsStep = useMemo(
    () =>
      function PlanDetailsStep({
        formData,
        setFormData,
        durationType,
        setDurationType,
        period,
        setPeriod,
        cycle,
        setCycle,
        cycleOptions,
        errors,
        setErrors,
        offerId,
        setOfferId,
        offerName,
        setOfferName,
        offerDiscount,
        setOfferDiscount,
        formatPriceWithSpaces,
        getBillingDescription,
        isSubmitting,
      }: CreateSubscriptionFormProps) {
        // Applied Offer card needs a plan name, a real price, and a chosen
        // billing cycle to show — usage limits play no part in this and
        // don't gate it.
        const priceNum = parseFloat(formData.price.replace(/\s/g, ''));
        const canShowPriceSummary =
          formData.planName.trim() !== '' &&
          !Number.isNaN(priceNum) &&
          priceNum > 0 &&
          cycle.trim() !== '';

        return (
          <>
            <div className="flex flex-col gap-2 mt-[28px]">
              <p className="text-[18px] text-on-surface-variant">
                Szczegóły planu
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 " data-field="subscription-planName">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Imię i nazwisko"
                    maxLength={200}
                    width={'50%'}
                    autoFocus={true}
                    value={formData.planName}
                    onChange={(e) => {
                      if (errors.planName) {
                        setErrors((prev) => ({
                          ...prev,
                          planName: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev) => ({
                        ...prev,
                        planName: next,
                      }));
                    }}
                    error={!!errors.planName}
                    errorMessage={errors.planName ?? ''}
                    disabled={isSubmitting}
                  />
                </div>
                <div data-field="subscription-description">
                  <Textarea
                    error={!!errors.description}
                    errorMessage={errors.description ?? ''}
                    label="Opis"
                    maxLength={500}
                    fullWidth
                    rows={4}
                    value={formData.description}
                    onChange={(e) => {
                      if (errors.description) {
                        setErrors((prev) => ({
                          ...prev,
                          description: '',
                        }));
                      }
                      setFormData((prev) => ({
                        ...prev,
                        description:
                          e.target.value.trimStart().slice(0, 500) || '',
                      }));
                    }}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-neutral-73" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Okres i rozliczenia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div
                    className="flex-1"
                    data-field="subscription-durationType"
                  >
                    <Dropdown
                      menuFullWidth
                      items={PERIOD_TYPE_OPTIONS}
                      selectedId={durationType}
                      error={!!errors.durationType}
                      errorMessage={errors.durationType ?? ''}
                      trigger={({
                        open,
                        toggle,
                        disabled,
                        readOnly,
                        error,
                      }) => (
                        <DropDownButton
                          data-testid="subscription-period-type"
                          label="Typ okresu"
                          value={durationType}
                          open={open}
                          toggle={toggle}
                          disabled={disabled}
                          readOnly={readOnly ?? false}
                          error={error}
                          hideChevron={readOnly ?? false}
                        />
                      )}
                      onItemClick={(id) => {
                        if (errors.durationType) {
                          setErrors((prev) => ({
                            ...prev,
                            durationType: '',
                          }));
                        }
                        setDurationType(id);
                      }}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1" data-field="subscription-period">
                    <Dropdown
                      key={durationType || 'empty'}
                      menuFullWidth
                      menuMaxVisibleItems={5}
                      items={
                        durationType === 'Miesięczny'
                          ? PERIOD_OPTIONS_MONTHLY
                          : PERIOD_OPTIONS_YEARLY
                      }
                      selectedId={period}
                      error={!!errors.period}
                      errorMessage={errors.period ?? ''}
                      trigger={({
                        open,
                        toggle,
                        disabled,
                        readOnly,
                        error,
                      }) => (
                        <DropDownButton
                          data-testid="subscription-period"
                          label="Okres"
                          value={period}
                          open={open}
                          toggle={toggle}
                          disabled={disabled}
                          readOnly={readOnly ?? false}
                          error={error}
                          hideChevron={readOnly ?? false}
                        />
                      )}
                      onItemClick={(id) => {
                        if (errors.period) {
                          setErrors((prev) => ({
                            ...prev,
                            period: '',
                          }));
                        }
                        setPeriod(id);
                      }}
                      disabled={durationType.length === 0 || isSubmitting}
                    />
                  </div>
                  <div className="flex-1" data-field="subscription-cycle">
                    {cycleOptions.length === 1 ? (
                      <TextField
                        label="Cykl rozliczeniowy"
                        value={cycle}
                        fullWidth={true}
                        disabled={isSubmitting}
                      />
                    ) : (
                      <Dropdown
                        key={`${durationType}-${period}`}
                        menuFullWidth
                        items={cycleOptions}
                        selectedId={cycle}
                        error={!!errors.cycle}
                        errorMessage={errors.cycle ?? ''}
                        trigger={({
                          open,
                          toggle,
                          disabled,
                          readOnly,
                          error,
                        }) => (
                          <DropDownButton
                            data-testid="subscription-billing-cycle"
                            label="Cykl rozliczeniowy"
                            value={cycle}
                            open={open}
                            toggle={toggle}
                            disabled={disabled}
                            readOnly={readOnly ?? false}
                            error={error}
                            hideChevron={readOnly ?? false}
                          />
                        )}
                        onItemClick={(id) => {
                          if (errors.cycle) {
                            setErrors((prev) => ({
                              ...prev,
                              cycle: '',
                            }));
                          }
                          setCycle(id);
                        }}
                        disabled={
                          durationType.length === 0 ||
                          period.length === 0 ||
                          isSubmitting
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-neutral-73" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Limity użytkowania
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div
                    className="flex flex-col gap-2 w-full"
                    data-field="subscription-userCount"
                  >
                    <TextField
                      label="Liczba użytkowników"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      fullWidth
                      value={formData.userCount}
                      onChange={(e) => {
                        if (errors.userCount) {
                          setErrors((prev) => ({
                            ...prev,
                            userCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({ ...prev, userCount: '' }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(500, Math.max(0, num));

                        setFormData((prev) => ({
                          ...prev,
                          userCount: String(clamped),
                        }));
                      }}
                      error={!!errors.userCount}
                      errorMessage={errors.userCount ?? ''}
                      disabled={isSubmitting}
                    />

                    {!errors.userCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        {
                          SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_MAX_USERS_HINT
                            .polish
                        }
                      </p>
                    )}
                  </div>
                  <div
                    className="flex flex-col gap-2 w-full"
                    data-field="subscription-facilityCount"
                  >
                    <TextField
                      label="Liczba placówek"
                      maxLength={2}
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={formData.facilityCount}
                      onChange={(e) => {
                        if (errors.facilityCount) {
                          setErrors((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(10, Math.max(1, num));

                        setFormData((prev) => ({
                          ...prev,
                          facilityCount: String(clamped),
                        }));
                      }}
                      error={!!errors.facilityCount}
                      errorMessage={errors.facilityCount ?? ''}
                      disabled={isSubmitting}
                    />

                    {!errors.facilityCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        {
                          SUBSCRIPTION_MANAGEMENT_LABELS
                            .FIELD_MAX_FACILITIES_HINT.polish
                        }
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label={
                        <>
                          Liczba dokumentów QMS{' '}
                          <span style={{ fontSize: 12 }}>
                            {
                              SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_OPTIONAL
                                .polish
                            }
                          </span>
                        </>
                      }
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      fullWidth={true}
                      value={formData.qmsDocCount}
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            qmsDocCount: '',
                          }));
                          return;
                        }
                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(2000, Math.max(0, num));
                        setFormData((prev) => ({
                          ...prev,
                          qmsDocCount: String(clamped),
                        }));
                      }}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-b-2 border-neutral-73" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">Cennik</p>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <div className="w-[32.7%]" data-field="subscription-price">
                    <TextField
                      fullWidth={true}
                      label="Cena"
                      maxLength={MAX_PRICE_LENGTH}
                      type="amount"
                      value={formatPriceWithSpaces(formData.price)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\s/g, '');
                        if (isPriceTooBig(raw)) {
                          return;
                        }
                        if (errors.price) {
                          setErrors((prev) => ({
                            ...prev,
                            price: '',
                          }));
                        }
                        setFormData((prev) => ({
                          ...prev,
                          price: raw,
                        }));
                      }}
                      error={!!errors.price}
                      errorMessage={errors.price ?? ''}
                      disabled={isSubmitting}
                      trailingIcon={
                        formData.price ? (
                          <span className="text-bodyLarge text-neutral-10 align-middle ml-[2px] mt-[12px]">
                            zł
                          </span>
                        ) : null
                      }
                    />
                  </div>
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {getBillingDescription(
                      formData.price,
                      cycle,
                      period,
                      formatPriceWithSpaces
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-neutral-73" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {SUBSCRIPTION_MANAGEMENT_LABELS.SECTION_OFFER.polish}
              </p>
              <div className="flex flex-col">
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 min-w-0">
                      <OfferInfiniteDropdown
                        value={offerId}
                        onChange={(id, offer) => {
                          setOfferId(id);
                          setOfferDiscount(
                            offer
                              ? {
                                  discountType: offer.discountType,
                                  discountValue: offer.discountValue,
                                }
                              : null
                          );
                          setOfferName(offer?.offerName ?? '');
                        }}
                        disabled={isSubmitting}
                        label={
                          <>
                            {
                              SUBSCRIPTION_MANAGEMENT_LABELS
                                .FIELD_SELECT_DISCOUNT.polish
                            }{' '}
                            <span style={{ fontSize: 12 }}>
                              {
                                SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_OPTIONAL
                                  .polish
                              }
                            </span>
                          </>
                        }
                        placement="top-start"
                        displayName={offerName}
                      />
                    </div>
                  </div>
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {
                      SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT_HELPER
                        .polish
                    }
                  </p>
                </div>
                {canShowPriceSummary && (
                  <div className="mt-8" data-testid="applied-offer-card">
                    <AppliedOfferCard
                      planName={formData.planName}
                      price={formData.price}
                      cycle={cycle}
                      discount={offerId ? offerDiscount : null}
                      formatPriceWithSpaces={formatPriceWithSpaces}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );
      },
    []
  );

  if (privilegesLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  if (!privileges.subscriptionCreate) {
    return <ErrorPage403 />;
  }

  return (
    <>
      <StepperPageShell
        as="form"
        onSubmit={handleFormSubmit}
        activeStep={isPreviewStep ? 'preview' : step}
        title={
          isPreviewStep
            ? SUBSCRIPTION_MANAGEMENT_LABELS.PREVIEW_PLAN_TITLE.polish
            : 'Utwórz plan'
        }
        stepper={
          !isPreviewStep && (
            <StepTabNav
              steps={STEP_TABS}
              currentStep={step}
              maxStep={maxReachedStep}
              completedSteps={completedSteps}
              onStepClick={handleTabClick}
            />
          )
        }
        footer={
          <>
            <Button
              label={SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_CANCEL.polish}
              buttonType="Square"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant hover:!border-neutral-73 hover:!bg-on-surface-variant/[0.08] !rounded-[8px]"
            />
            <div className="flex items-center gap-4">
              {isPreviewStep ? (
                <Button
                  label={
                    SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_BACK_TO_EDIT.polish
                  }
                  buttonType="Square"
                  type="button"
                  onClick={handleBackToEdit}
                  disabled={isSubmitting}
                  className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant border border-neutral-73 hover:!bg-on-surface-variant/[0.08] !rounded-[8px]"
                />
              ) : (
                step > 1 && (
                  <Button
                    label={SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_BACK.polish}
                    buttonType="Square"
                    type="button"
                    onClick={() => handleTabClick(step - 1)}
                    disabled={isSubmitting}
                    className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant border border-neutral-73 hover:!bg-on-surface-variant/[0.08] !rounded-[8px]"
                  />
                )
              )}
              <Button
                label={
                  isSubmitting
                    ? 'Creating...'
                    : step === STEP_TABS.length
                      ? SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_CREATE_PLAN.polish
                      : SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_NEXT.polish
                }
                type="submit"
                disabled={isSubmitting}
                className="!h-[40px] text-labelLarge cursor-pointer !rounded-[8px]"
              />
            </div>
          </>
        }
      >
        {/* Both steps stay mounted; only visibility toggles, so switching
            steps no longer remounts (and flickers) either one. */}
        <div
          className={`flex flex-col gap-8 ${step === 1 && !isPreviewStep ? '' : 'hidden'}`}
        >
          <PlanDetailsStep
            formData={formData}
            setFormData={setFormData}
            durationType={durationType}
            setDurationType={setDurationType}
            period={period}
            setPeriod={setPeriod}
            cycle={cycle}
            setCycle={setCycle}
            cycleOptions={cycleOptions}
            errors={errors}
            setErrors={setErrors}
            offerId={offerId}
            setOfferId={setOfferId}
            offerName={offerName}
            setOfferName={setOfferName}
            offerDiscount={offerDiscount}
            setOfferDiscount={setOfferDiscount}
            formatPriceWithSpaces={formatPriceWithSpaces}
            getBillingDescription={getBillingDescription}
            isSubmitting={isSubmitting}
          />
        </div>
        <div
          className={`flex flex-col gap-6 mt-[28px] ${step === 2 && !isPreviewStep ? '' : 'hidden'}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-[18px] text-on-surface-variant">
                {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_SERVICES.polish}
              </p>
              <p className="text-bodySmall text-on-surface-variant">
                At least one service with a usage limit is required.
              </p>
            </div>
            <Tooltip
              content="Fill in all required fields to enable Add Service."
              forceShow={hasIncompleteService}
              useContentWidth
              contentMaxWidth={300}
              side="left"
              triggerWrapContent
            >
              <Button
                type="button"
                label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_ADD_SERVICE.polish}
                buttonType="Square"
                size="Small"
                onClick={handleAddService}
                disabled={isSubmitting || hasIncompleteService}
                customBorderColor="var(--neutral-73)"
                customBgColor="transparent"
                customTextColor="var(--color-on-surface-variant)"
                customFontWeight={500}
                icon={<PlusIcon className="w-5 h-5" />}
                showIcon
                className="cursor-pointer hover:!bg-[#F0F0F0]"
              />
            </Tooltip>
          </div>

          {servicesError && (
            <p
              data-field="subscription-services-error"
              data-testid="subscription-services-error"
              className="text-bodySmall text-error-40"
            >
              {servicesError}
            </p>
          )}

          <div className="flex flex-col gap-6">
            {services.map((entry) => (
              <ServiceConfigCard
                key={entry.key}
                entry={entry}
                expanded={expandedServiceKey === entry.key}
                onToggleExpand={() => handleToggleExpandService(entry.key)}
                onServiceChange={(serviceTypeId, serviceName) =>
                  handleServiceTypeChange(entry.key, serviceTypeId, serviceName)
                }
                onFieldChange={(field, value) =>
                  handleServiceFieldChange(entry.key, field, value)
                }
                onRemove={() => handleRequestRemoveService(entry.key)}
                excludedServiceTypeIds={services
                  .filter((s) => s.key !== entry.key && s.serviceTypeId)
                  .map((s) => s.serviceTypeId)}
                isOnlyService={services.length === 1}
                errors={serviceFieldErrors[entry.key]}
                disabled={isSubmitting}
              />
            ))}
          </div>
        </div>

        {step === 3 && !isPreviewStep && (
          <div className="flex flex-col gap-6 mt-[28px]">
            <div className="flex flex-col gap-1">
              <p
                style={{
                  color: 'var(--Schemes-On-Surface-Variant, #474A4C)',
                  fontSize: 18,
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  lineHeight: '26px',
                  wordWrap: 'break-word',
                }}
              >
                {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_EQUIPMENT_PRICING.polish}
              </p>
              <p
                style={{
                  color: 'var(--Schemes-On-Surface-Variant, #474A4C)',
                  fontSize: 12,
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  lineHeight: '16px',
                  letterSpacing: 0.4,
                  wordWrap: 'break-word',
                }}
              >
                {
                  SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_EQUIPMENT_PRICING_HELPER
                    .polish
                }
              </p>
            </div>

            {equipmentCategoriesLoading ? (
              <Loader />
            ) : (
              <div className="flex flex-col gap-5">
                {equipmentCategoryOptions.map((category) => (
                  <EquipmentCategoryPriceRow
                    key={category.id}
                    categoryId={category.id}
                    categoryLabel={category.label}
                    basePrice={equipmentPrices[category.id] ?? ''}
                    onBasePriceChange={(value) =>
                      handleEquipmentPriceChange(category.id, value)
                    }
                    error={equipmentPriceErrors[category.id]}
                    disabled={isSubmitting}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {step === 4 && !isPreviewStep && (
          <div className="flex flex-col gap-6 mt-[28px]">
            <div className="flex flex-col gap-1">
              <p
                style={{
                  color: 'var(--Schemes-On-Surface-Variant, #474A4C)',
                  fontSize: 18,
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  lineHeight: '26px',
                  wordWrap: 'break-word',
                }}
              >
                {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_DISCOUNT_LOGIC.polish}
              </p>
              <p
                style={{
                  color: 'var(--Schemes-On-Surface-Variant, #474A4C)',
                  fontSize: 12,
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  lineHeight: '16px',
                  letterSpacing: 0.4,
                  wordWrap: 'break-word',
                }}
              >
                {
                  SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT_TIERS_HELPER
                    .polish
                }
              </p>
            </div>

            <div className="flex flex-col gap-5">
              {Array.from(
                { length: EQUIPMENT_RANK_COUNT },
                (_, i) => i + 1
              ).map((rank) => (
                <DiscountTierRow
                  key={rank}
                  rank={rank}
                  discountPercentage={discountTiers[rank] ?? ''}
                  onDiscountPercentageChange={(value) =>
                    handleDiscountTierChange(rank, value)
                  }
                  error={discountTierErrors[rank]}
                  disabled={isSubmitting}
                />
              ))}
            </div>
          </div>
        )}

        {isPreviewStep && (
          <div className="mt-[28px]">
            <PlanPreviewAccordion data={previewData} />
          </div>
        )}
      </StepperPageShell>

      {/*
        Old single-page (non-wizard) layout — kept for reference, not deleted,
        per stakeholder request. Superseded by the StepperPageShell/StepTabNav
        wizard above (RR-1071). `isPreviewStep` no longer exists on the hook,
        replaced by `step`.

      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={CreateSubscriptionHeader}
        cancelButtonLabel={isPreviewStep ? 'Wróć do edycji' : 'Anuluj'}
        submitButtonLabel={isPreviewStep ? 'Utwórz plan' : 'Przejdź do podglądu'}
        submitButtonLoadingLabel="Tworzenie planu..."
      >
        <CreateSubscriptionForm ... />
      </CreateLayout>
      */}

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarError}
        variant="error"
        onClose={handleCloseSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton={true}
      />

      <AddUserModal
        open={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirmCancel={handleConfirmCancel}
      />

      <RemoveServiceDialog
        open={removeDialogEntry !== null}
        onClose={handleCloseRemoveDialog}
        serviceName={removeDialogEntry?.serviceName || 'this service'}
        onConfirm={handleConfirmRemoveService}
      />
    </>
  );
};

export default CreateSubscription;
